import { Working } from '@ui/Working';
import { ShareQuestionAndAnswer } from '@containers/DigitalAnalyst/components/ShareQuestionAndAnswer';
import { ResultRatingForm } from '@containers/form/ResultRatingForm';
import { ResultContext, RUNNING_STATUS, CITATIONS_STATUS } from '@/contexts/ResultContext';
import { Body5, Container } from '@ui';
import React from 'react';
import { Citations } from './components/Citations';
import { Answer } from './components/Answer';
import { RefiningAnswer } from './components/RefiningAnswer';
import { AnswerConfigContext } from '@/contexts/AnswerConfigContext';
import { Followup } from './components/Followup';
import { ContextSources } from './components/ContextSources';
import { NewsSources } from './components/NewsSources';
import { ModeContext } from '@/contexts/ModeContext';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';
import styled from 'styled-components';
import { QueryControllerContext } from '@/contexts/QueryControllerContext';
import { answerFormatter } from '@classes/AnswerFormatter';
import { CitationsContextProvider } from '@/contexts/CitationsContext';

const ActionsWrapper = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 16px;

  padding-top: 16px;
`;
const ForceButtonWidth = styled.div`
  button {
    width: 240px;
  }
`;

const AddCitationsButton = () => {
  const { resultId, queryRunId, runningStatus, getCitations, citationsStatus } = React.useContext(ResultContext);
  const [queueCitations, setQueueCitations] = React.useState(false);

  const isStreaming = runningStatus === RUNNING_STATUS.RUNNING || runningStatus === RUNNING_STATUS.REFINING;

  React.useEffect(() => {
    if (queueCitations && !isStreaming && queryRunId) {
      setQueueCitations(false);
      getCitations(queryRunId);
    }
  }, [queueCitations, isStreaming, queryRunId, getCitations]);

  React.useEffect(() => {
    setQueueCitations(false);
  }, [resultId]);

  const handleClick = () => {
    if (isStreaming) {
      setQueueCitations(true);
    } else if (queryRunId) {
      getCitations(queryRunId);
    }
  };

  const label = queueCitations
    ? 'Queued Citations'
    : citationsStatus === CITATIONS_STATUS.RUNNING
    ? 'Adding Citations...'
    : citationsStatus === CITATIONS_STATUS.COMPLETED
    ? 'Re-run Citations'
    : 'Add Citations';

  return (
    <SkeletonButton
      onClick={handleClick}
      disabled={queueCitations || citationsStatus === CITATIONS_STATUS.RUNNING || (!isStreaming && !queryRunId)}
    >
      {label}
    </SkeletonButton>
  );
};

const QueryStreamingResult = () => {
  const { enableFollowUpQuestions, enableSearchForMoreDetail, enableCitations } = React.useContext(ModeContext);
  const { enableRating, enableShare } = React.useContext(AnswerConfigContext);
  const { resultId, runningStatus, answer } = React.useContext(ResultContext);
  const { prevQueryFormData, getMoreDetailForCurrentResult } = React.useContext(QueryControllerContext);

  const handleGetMoreDetailedResultClick = () => {
    const answerWithoutFollowUpQuestions = answerFormatter.removeFollowUpQuestions(answer);
    getMoreDetailForCurrentResult(answerWithoutFollowUpQuestions, {
      displayQuestion: 'Improve the previous answer by including more details',
    });
  };
  const handleReviseQuestion = () => {
    const questionInput = document.getElementById('question-input');
    if (questionInput) {
      questionInput.focus();
    }
  };

  return (
    <Container>
      <Container bottom={16}>
        <Body5 bold>Answer:</Body5>
      </Container>
      {runningStatus === RUNNING_STATUS.REFINING && (
        <Container>
          <RefiningAnswer />
        </Container>
      )}
      {runningStatus === RUNNING_STATUS.RUNNING && (
        <Container>
          <Working />
        </Container>
      )}
      <CitationsContextProvider>
        <Container>
          <Citations />
        </Container>
        <Container left={16}>
          <Answer />
        </Container>
      </CitationsContextProvider>
      {enableFollowUpQuestions && <Followup />}
      <ActionsWrapper>
        {enableSearchForMoreDetail === true && (
          <ForceButtonWidth>
            <SkeletonButton onClick={handleGetMoreDetailedResultClick}>Search for more detail</SkeletonButton>
          </ForceButtonWidth>
        )}
        {enableCitations === true && (
          <ForceButtonWidth>
            <AddCitationsButton />
          </ForceButtonWidth>
        )}
        <ForceButtonWidth>
          <SkeletonButton onClick={handleReviseQuestion}>Revise Question</SkeletonButton>
        </ForceButtonWidth>
      </ActionsWrapper>
      {runningStatus === RUNNING_STATUS.COMPLETED && enableShare && (
        <Container top={16}>
          <ShareQuestionAndAnswer question={prevQueryFormData.question} answer={answer} buttonWidth="240px" />
        </Container>
      )}
      <ContextSources top={16} />
      <NewsSources top={16} />
      {runningStatus === RUNNING_STATUS.COMPLETED && enableRating && (
        <Container top={16}>
          <ResultRatingForm resultId={resultId} />
        </Container>
      )}
      <Container top={16} />
    </Container>
  );
};

export default QueryStreamingResult;
